import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/base/SEOHead';
import { localBusinessSchema, buildBreadcrumbSchema, buildGraphSchema } from '@/components/base/schemas';
import { Link } from 'react-router-dom';
import { useState, FormEvent } from 'react';

const breadcrumbSchema = buildBreadcrumbSchema([
  { name: 'Accueil', item: 'https://fenetre-paris15.fr/' },
  { name: 'Contact', item: 'https://fenetre-paris15.fr/contact/' },
]);

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="company_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setSubmitted(true);
      return;
    }

    setLoading(true);
    const formData = new FormData(form);
    try {
      const data: Record<string, string> = {};
      formData.forEach((value, key) => { data[key] = value as string; });
      const res = await fetch('https://formspree.io/f/mrewqkaw', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Erreur réseau');
      setSubmitted(true);
      form.reset();
    } catch {
      setError('Une erreur est survenue. Veuillez réessayer ou nous appeler directement.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <SEOHead
        title="Contact — Réparation Fenêtre Paris 15 | DF PRO"
        description="Contactez DF PRO pour la réparation de vos fenêtres dans le 15ᵉ. Devis gratuit, intervention 24/48h. ☎ 01 87 66 81 55"
        canonicalUrl="https://fenetre-paris15.fr/contact/"
        schema={buildGraphSchema(localBusinessSchema, breadcrumbSchema)}
      />

      <div className="bg-brand-mint/30 py-3">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav aria-label="Fil d'Ariane" className="text-xs text-brand-text/60">
            <Link to="/" className="hover:text-brand-accent">Accueil</Link><span className="mx-2">/</span><span className="text-brand-accent font-medium">Contact</span>
          </nav>
        </div>
      </div>

      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-brand-dark text-center mb-4">Contactez DF PRO — Paris 15ᵉ</h1>
          <p className="text-brand-text/70 text-center mb-10 max-w-2xl mx-auto">
            Une question, un diagnostic, un devis ? Nous sommes à votre écoute. DF PRO intervient dans tout le 15ᵉ arrondissement.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-12">
            {/* Contact info cards */}
            <div className="bg-brand-mint/30 rounded-xl p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-brand-accent text-white flex items-center justify-center">
                <i className="ri-phone-line text-xl"></i>
              </div>
              <h3 className="font-heading font-bold text-brand-dark mb-2">Téléphone</h3>
              <a href="tel:+33187668155" className="text-brand-accent font-semibold hover:underline">01 87 66 81 55</a>
              <p className="text-brand-text/50 text-xs mt-2">Lun–Ven 8h–19h, Sam 9h–14h</p>
            </div>
            <div className="bg-brand-mint/30 rounded-xl p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-brand-accent text-white flex items-center justify-center">
                <i className="ri-map-pin-line text-xl"></i>
              </div>
              <h3 className="font-heading font-bold text-brand-dark mb-2">Zone d'intervention</h3>
              <p className="text-brand-text/70 text-sm">Paris 15ᵉ (75015) et tous ses quartiers</p>
              <Link to="/zones-intervention-paris-15/" className="text-brand-accent text-sm hover:underline mt-1 block">Voir les zones</Link>
            </div>
            <div className="bg-brand-mint/30 rounded-xl p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-brand-accent text-white flex items-center justify-center">
                <i className="ri-time-line text-xl"></i>
              </div>
              <h3 className="font-heading font-bold text-brand-dark mb-2">Horaires</h3>
              <div className="text-brand-text/70 text-sm space-y-1">
                <p>Lundi – Vendredi : 8h00 – 19h00</p>
                <p>Samedi : 9h00 – 14h00</p>
                <p className="text-brand-text/40">Dimanche : Fermé</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Form */}
            <div>
              {submitted ? (
                <div className="bg-brand-mint/50 rounded-2xl p-8 text-center animate-fade-in">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-brand-accent text-white flex items-center justify-center">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-brand-dark mb-3">Message envoyé !</h2>
                  <p className="text-brand-text/70">Merci de nous avoir contactés. DF PRO vous répond dans les plus brefs délais.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="" className="bg-brand-mint/20 rounded-2xl p-6 md:p-8">
                  {error && <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">{error}</div>}
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-brand-dark mb-1.5">Nom *</label>
                      <input type="text" id="contact-name" name="name" required className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-brand-text focus:outline-none focus:border-brand-accent transition-colors" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-brand-dark mb-1.5">E-mail</label>
                        <input type="email" id="contact-email" name="email" className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-brand-text focus:outline-none focus:border-brand-accent transition-colors" />
                      </div>
                      <div>
                        <label htmlFor="contact-phone" className="block text-sm font-medium text-brand-dark mb-1.5">Téléphone</label>
                        <input type="tel" id="contact-phone" name="phone" className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-brand-text focus:outline-none focus:border-brand-accent transition-colors" />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-brand-dark mb-1.5">Message *</label>
                      <textarea id="contact-message" name="message" rows={4} required maxLength={500} className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-brand-text focus:outline-none focus:border-brand-accent transition-colors resize-none"></textarea>
                      <p className="text-xs text-brand-text/40 mt-1">500 caractères maximum</p>
                    </div>
                    <input type="hidden" name="_subject" value="Nouvelle demande - Contact - fenetre-paris15.fr" />
                    <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hp-field" />
                    <button type="submit" disabled={loading} className="btn-primary w-full justify-center text-sm py-3 disabled:opacity-50">
                      {loading ? <><i className="ri-loader-4-line animate-spin"></i> Envoi...</> : <><i className="ri-send-plane-line"></i> Envoyer le message</>}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Map / Info */}
            <div className="flex flex-col gap-6">
              {/* Image d'ambiance */}
              <div className="aspect-[16/10] rounded-2xl overflow-hidden">
                <img
                  src="/images/dfpro-contact.webp"
                  alt="Atelier DF PRO — intervention réparation fenêtres Paris 15"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="bg-brand-mint/20 rounded-2xl p-6 md:p-8">
                <h3 className="font-heading font-bold text-brand-dark text-lg mb-3">DF PRO</h3>
                <p className="text-brand-text/70 text-sm mb-1">Spécialiste réparation fenêtres et menuiseries</p>
                <p className="text-brand-text/70 text-sm mb-1">Paris 15ᵉ (75015)</p>
                <a href="tel:+33187668155" className="text-brand-accent font-semibold text-sm">01 87 66 81 55</a>
              </div>
              <div className="bg-brand-mint/20 rounded-2xl p-6 md:p-8 flex-1">
                <h3 className="font-heading font-bold text-brand-dark text-lg mb-3">Intervention dans tout le 15ᵉ</h3>
                <p className="text-brand-text/70 text-sm leading-relaxed">
                  DF PRO couvre l'ensemble du 15ᵉ arrondissement : Vaugirard, Grenelle, Javel, Beaugrenelle, Saint-Lambert, Necker, Convention, Commerce, Cambronne, Pasteur, Falguière, Front de Seine, Porte de Versailles. Nous nous déplaçons gratuitement pour tout diagnostic.
                </p>
              </div>
              <div className="aspect-[16/9] rounded-2xl overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d21005.85685806444!2d2.2742!3d48.8413!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e6701b1f5e0e0b%3A0x40b82c3688b3400!2s75015%20Paris!5e0!3m2!1sfr!2sfr!4v1!5m2!1sfr!2sfr"
                  width="100%"
                  height="100%"
                  className="border-0"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Paris 15ᵉ - Zone d'intervention DF PRO"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}